import axios from 'axios'

const BASE_URL = process.env.REACT_APP_NEXTERA_API

export type ReviewTaskStatus = 'not_started' | 'in_progress' | 'completed'
export type ReviewTaskPointFilter = 'none' | 'past_7_days'
export type ReviewTaskPointSequence = 'id' | 'robot'

export interface ReviewTaskLastWorkedBy {
  id: number
  name: string
  email: string
}

export interface ReviewTask {
  id: number
  status: ReviewTaskStatus
  started_at: string | null
  completed_at: string | null
  point_filter: ReviewTaskPointFilter
  point_sequence: ReviewTaskPointSequence
  ai_assist: boolean
  floor_ids: number[]
  floors_label: string
  tracker_ids: number[]
  trackers_label: string
  assignee_ids: number[]
  assignees_label: string
  last_worked_by: ReviewTaskLastWorkedBy | null
  claimed_by?: ReviewTaskLastWorkedBy | null
  claimed_at?: string | null
  points_total: number
  points_viewed: number
  suggestion_stops_viewed?: number
  suggestion_stops_total?: number
  active_seconds?: number
  changes_made: number
  activity_log_count?: number
  review_active_seconds?: number
  ai_batch?: ProgressAiBatch | null
  registered_on: string
  last_modified_on: string
}

export interface ReviewTaskAssigneeOption {
  id: number
  name: string
  email: string
}

export interface ReviewTaskWritePayload {
  status?: ReviewTaskStatus
  point_filter?: ReviewTaskPointFilter
  point_sequence?: ReviewTaskPointSequence
  ai_assist?: boolean
  floor_ids?: number[]
  tracker_ids?: number[]
  assignee_ids?: number[]
}

export const REVIEW_TASK_PAGE_SIZE = 25

export interface ReviewTaskListMeta {
  next?: string | null
  previous?: string | null
  count?: number
  next_offset?: number
  pending_count?: number
}

export interface ReviewTaskListResponse {
  data: ReviewTask[]
  meta: ReviewTaskListMeta
}

export const listReviewTasks = (
  projectId: string,
  options?: {
    status?: ReviewTaskStatus | 'all'
    ai_assist?: boolean
    limit?: number
    offset?: number
  },
): Promise<ReviewTaskListResponse> => {
  const params: Record<string, string | number | boolean> = {
    limit: options?.limit ?? REVIEW_TASK_PAGE_SIZE,
    offset: options?.offset ?? 0,
  }
  if (options?.status && options.status !== 'all') {
    params.status = options.status
  }
  if (options?.ai_assist != null) {
    params.ai_assist = options.ai_assist ? 'true' : 'false'
  }
  return axios
    .get(`${BASE_URL}/project/${projectId}/review-tasks`, { params })
    .then(r => r.data)
}

export const createReviewTask = (
  projectId: string,
  payload: ReviewTaskWritePayload = {},
): Promise<ReviewTask> => {
  return axios
    .post(`${BASE_URL}/project/${projectId}/review-task`, payload)
    .then(r => r.data)
    .then(r => r.data)
}

export const updateReviewTask = (
  projectId: string,
  taskId: number,
  payload: ReviewTaskWritePayload,
): Promise<ReviewTask> => {
  return axios
    .patch(`${BASE_URL}/project/${projectId}/review-task/${taskId}`, payload)
    .then(r => r.data)
    .then(r => r.data)
}

export const deleteReviewTask = (
  projectId: string,
  taskId: number,
): Promise<ReviewTask> => {
  return axios
    .delete(`${BASE_URL}/project/${projectId}/review-task/${taskId}`)
    .then(r => r.data)
    .then(r => r.data)
}

export const cloneReviewTask = (
  projectId: string,
  taskId: number,
): Promise<ReviewTask> => {
  return axios
    .post(`${BASE_URL}/project/${projectId}/review-task/${taskId}/clone`)
    .then(r => r.data)
    .then(r => r.data)
}

export const setReviewTaskStatus = (
  projectId: string,
  taskId: number,
  status: ReviewTaskStatus,
): Promise<ReviewTask> => {
  return axios
    .post(`${BASE_URL}/project/${projectId}/review-task/${taskId}/status`, {
      status,
    })
    .then(r => r.data)
    .then(r => r.data)
}

export const listReviewTaskAssignees = (
  projectId: string,
): Promise<ReviewTaskAssigneeOption[]> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/review-tasks/assignees`)
    .then(r => r.data)
    .then(r => r.data)
}

export type ReviewVisitAction = 'confirmed' | 'skipped'

export interface ReviewQueuePoint {
  point_id: number
  viewpoint_pk: number
  floor_code: string
  floor_id: string
  latest_image_id: number | null
  latest_taken_on: string | null
  previous_image_id: number | null
  previous_taken_on: string | null
  visited_action: ReviewVisitAction | null
}

export interface ReviewQueueTracker {
  id: number
  name: string
}

export interface ReviewQueueMeta {
  task_id: number
  status: ReviewTaskStatus
  ai_assist?: boolean
  floors_label: string
  trackers_label: string
  trackers: ReviewQueueTracker[]
  points_total: number
  points_viewed: number
  suggestion_stops_viewed?: number
  suggestion_stops_total?: number
  active_seconds?: number
  changes_made: number
  started_at?: string | null
  completed_at?: string | null
  claimed_by?: ReviewTaskLastWorkedBy | null
}

export interface ReviewQueueResponse {
  data: ReviewQueuePoint[]
  meta: ReviewQueueMeta
}

export const getReviewTaskQueue = (
  projectId: string,
  taskId: number | string,
  options?: { metaOnly?: boolean },
): Promise<ReviewQueueResponse> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/review-task/${taskId}/queue`, {
      params: options?.metaOnly ? { meta_only: 1 } : undefined,
    })
    .then(r => r.data)
}

export interface ReviewTaskClaim {
  claim_token: string
  active_seconds: number
  claimed_by: ReviewTaskLastWorkedBy | null
  claimed_at?: string | null
}

export const claimReviewTask = (
  projectId: string,
  taskId: number | string,
): Promise<ReviewTaskClaim> =>
  axios
    .post(`${BASE_URL}/project/${projectId}/review-task/${taskId}/claim`)
    .then(r => unwrapData(r.data))

export const heartbeatReviewTask = (
  projectId: string,
  taskId: number | string,
  payload: { claim_token: string; accumulated_seconds: number },
): Promise<{ active_seconds: number; claim_token?: string }> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/heartbeat`,
      payload,
    )
    .then(r => unwrapData(r.data))

export const releaseReviewTask = (
  projectId: string,
  taskId: number | string,
  payload: { claim_token: string; accumulated_seconds: number },
): Promise<void> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/release`,
      payload,
    )
    .then(() => undefined)

/** Tab-close / pagehide: keepalive fetch so Authorization still goes out. */
export const beaconReleaseReviewTask = (
  projectId: string,
  taskId: number | string,
  payload: { claim_token: string; accumulated_seconds: number },
) => {
  const url = `${BASE_URL}/project/${projectId}/review-task/${taskId}/release`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  const auth = axios.defaults.headers.common.Authorization
  if (typeof auth === 'string' && auth) {
    headers.Authorization = auth
  }
  try {
    void fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'include',
    })
  } catch {
    /* ignore unload failures */
  }
}

export interface ReviewVisitResponse {
  viewpoint_pk: number
  action: ReviewVisitAction
  changes_made: number
  completed: boolean
  status: ReviewTaskStatus
  points_viewed: number
  points_total: number
  task_changes_made: number
}

export const postReviewTaskVisit = (
  projectId: string,
  taskId: number | string,
  payload: {
    viewpoint_pk: number
    action: ReviewVisitAction
    changes_made?: number
  },
): Promise<ReviewVisitResponse> => {
  return axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/visit`,
      payload,
    )
    .then(r => r.data)
    .then(r => r.data)
}

export const postReviewTaskObservations = (
  projectId: string,
  taskId: number | string,
  payload: {
    image: number
    job_ids: number[]
    observed_quantity: 0 | 1
  },
): Promise<{ written: number }> => {
  return axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/observations`,
      payload,
    )
    .then(r => r.data)
    .then(r => r.data)
}

/** Slim STL payload for the review 360 overlay (viewpoint-scoped). */
export interface ReviewViewpointStl {
  job_id: number
  item_id: number
  stl_file_key: string
  stl_url: string
  display_color: string
  project_tracker: number
}

export const getReviewTaskViewpointStls = (
  projectId: string,
  taskId: number | string,
  viewpointPk: number,
): Promise<ReviewViewpointStl[]> => {
  return axios
    .get(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/viewpoint-stls`,
      { params: { viewpoint_pk: viewpointPk } },
    )
    .then(r => r.data)
    .then(r => r.data || [])
}

export type ProgressAiBatchStatus =
  | 'not_started'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'

export type ProgressAiPointStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'cancelled'

export interface ProgressAiBatch {
  id: number
  review_task_id: number
  status: ProgressAiBatchStatus
  model: string
  verify_installed: boolean
  send_examples: boolean
  lessons: string
  points_total: number
  points_done: number
  points_failed: number
  points_skipped: number
  cost_usd: number
  percent_complete: number
  eta_seconds: number | null
  forecast_cost_usd: number | null
  started_at: string | null
  completed_at: string | null
}

export interface ProgressAiPointResult {
  id: number
  batch_id: number
  review_task_id: number
  viewpoints_point_id: number
  floor_code: string
  point_sub_id: number
  status: ProgressAiPointStatus
  skipped_reason?: string | null
  error?: string | null
  suggestions: Array<{
    item_id: number
    tracker: string
    job_type: string
    change: string
    confidence: string
    evidence: string
  }>
  notes: string
  usage?: {
    cost_usd?: number
    input_tokens?: number
    output_tokens?: number
  } | null
  image_id?: number | null
  taken_on?: string | null
  raw?: string | null
}

export interface ProgressAiBatchOptions {
  model?: string
  verify_installed?: boolean
  send_examples?: boolean
  lessons?: string
  from_point_sub_id?: number
  only_point_sub_id?: number
}

const unwrapData = <T>(payload: { data?: T } | T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data ?? (payload as T)
  }
  return payload as T
}

export const getReviewTaskAiBatch = (
  projectId: string,
  taskId: number,
): Promise<{ batch: ProgressAiBatch | null; lessons?: string }> =>
  axios
    .get(`${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch`)
    .then(r => unwrapData(r.data))

export const startReviewTaskAiBatch = (
  projectId: string,
  taskId: number,
  options: ProgressAiBatchOptions = {},
): Promise<{ batch: ProgressAiBatch }> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/start`,
      options,
    )
    .then(r => unwrapData(r.data))

export const continueReviewTaskAiBatch = (
  projectId: string,
  taskId: number,
  options: { from_point_sub_id?: number } = {},
): Promise<{ batch: ProgressAiBatch }> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/continue`,
      options,
    )
    .then(r => unwrapData(r.data))

export const stopReviewTaskAiBatch = (
  projectId: string,
  taskId: number,
): Promise<{ batch: ProgressAiBatch }> =>
  axios
    .post(`${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/stop`)
    .then(r => unwrapData(r.data))

export const updateReviewTaskAiBatch = (
  projectId: string,
  taskId: number,
  options: ProgressAiBatchOptions,
): Promise<{ batch: ProgressAiBatch | null; lessons?: string }> =>
  axios
    .patch(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch`,
      options,
    )
    .then(r => unwrapData(r.data))

export const listReviewTaskAiBatchPoints = (
  projectId: string,
  taskId: number,
): Promise<{
  batch: ProgressAiBatch | null
  lessons?: string
  points: ProgressAiPointResult[]
  task?: {
    id: number
    floors_label: string
    registered_on: string | null
  }
}> =>
  axios
    .get(`${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/points`)
    .then(r => unwrapData(r.data))

export const getReviewTaskAiBatchPoint = (
  projectId: string,
  taskId: number,
  pointResultId: number,
  options?: { includeArtifacts?: boolean },
): Promise<{
  batch: ProgressAiBatch
  point: ProgressAiPointResult
  artifacts?: {
    zenith: {
      data?: string
      url?: string
      media_type?: string
      mediaType?: string
    }
    minimaps: Array<{
      tracker: string
      image: {
        data?: string
        url?: string
        media_type?: string
        mediaType?: string
      }
    }>
    item_facts: unknown[]
  }
  artifacts_error?: string
}> =>
  axios
    .get(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/points/${pointResultId}`,
      {
        params: options?.includeArtifacts
          ? { include_artifacts: true }
          : undefined,
      },
    )
    .then(r => unwrapData(r.data))

export const rerunReviewTaskAiBatchPoint = (
  projectId: string,
  taskId: number,
  pointResultId: number,
): Promise<{ point: ProgressAiPointResult }> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/points/${pointResultId}/rerun`,
    )
    .then(r => unwrapData(r.data))

export const sendReviewTaskAiBatchPointFeedback = (
  projectId: string,
  taskId: number,
  pointResultId: number,
  feedback: string,
): Promise<{ point: ProgressAiPointResult; batch: ProgressAiBatch }> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/points/${pointResultId}/feedback`,
      { feedback },
    )
    .then(r => unwrapData(r.data))

export const saveReviewTaskAiBatchPointLesson = (
  projectId: string,
  taskId: number,
  pointResultId: number,
  feedback: string,
): Promise<{ batch: ProgressAiBatch; lesson_line: string }> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/ai-batch/points/${pointResultId}/save-lesson`,
      { feedback },
    )
    .then(r => unwrapData(r.data))

export type ReviewActivityEventType =
  | 'item_marked_installed'
  | 'item_marked_uninstalled'
  | 'ui_stl_overlay_toggled'
  | 'ui_previous_image_overlay_toggled'
  | 'ai_suggestion_not_applied'
  | 'point_navigated'

export interface ReviewActivityLogEvent {
  id: number
  review_task_id: number
  viewpoints_point_id: number
  point_sub_id?: number | null
  floor_code?: string | null
  user_id: number
  user_label: string
  event_type: ReviewActivityEventType | string
  project_item_id?: number | null
  project_job_id?: number | null
  project_tracker_id?: number | null
  tracker_name?: string | null
  job_type_name?: string | null
  job_type_slug?: string | null
  observed_quantity?: number | null
  ai_suggestion_change?: string | null
  ai_suggestion_confidence?: string | null
  ai_suggestion_evidence?: string | null
  ai_point_result_id?: number | null
  metadata?: Record<string, unknown> | null
  registered_on?: string | null
}

export const postReviewTaskActivityLog = (
  projectId: string,
  taskId: number | string,
  payload: {
    viewpoint_pk: number
    events: Array<{
      event_type: ReviewActivityEventType
      metadata?: Record<string, unknown>
      project_tracker_id?: number
    }>
  },
): Promise<{ written: number }> =>
  axios
    .post(
      `${BASE_URL}/project/${projectId}/review-task/${taskId}/activity-log`,
      payload,
    )
    .then(r => r.data)

export const listReviewTaskActivityLog = (
  projectId: string,
  taskId: number | string,
  options?: {
    viewpoint_pk?: number
    event_type?: string
    item_id?: number
    limit?: number
    offset?: number
  },
): Promise<{
  data: ReviewActivityLogEvent[]
  meta?: {
    count?: number
    next_offset?: number | null
    review_active_seconds?: number
  }
}> =>
  axios
    .get(`${BASE_URL}/project/${projectId}/review-task/${taskId}/activity-log`, {
      params: {
        viewpoint_pk: options?.viewpoint_pk,
        event_type: options?.event_type,
        item_id: options?.item_id,
        limit: options?.limit ?? 200,
        offset: options?.offset ?? 0,
      },
    })
    .then(r => r.data)
